'use client'

import { useLayoutEffect, useMemo, useState } from 'react'

import { documentationNavUrlPathForSection } from '@/lib/github/documentationSlug'
import type { CategoryNode, TutorialSection } from '@/lib/github/types'
import { ACADEMY_ROUTES, buildDocumentationHref } from '@/shared/academy.const'
import { DOCUMENTATION_MENU_TEST_IDS } from '@/shared/academy-test-ids.const'
import { AcademyBackLink } from '@/shared/components/AcademyBackLink'
import { useDocumentationLanguageContext } from '@/shared/components/DocumentationLayoutShell/context/documentationLanguageContext'

import styles from './DocumentationMenu.module.scss'

const ChevronRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

interface DocumentationMenuProps {
  sections: TutorialSection[]
  currentPath?: string
  categoryTree?: CategoryNode[]
  rootArticles?: TutorialSection[]
  basePath?: string
  title?: string
}

function getParentCategoryPaths(categoryTree: CategoryNode[], currentPath?: string) {
  if (!currentPath) return []

  const parentPaths: string[] = []

  const searchCategory = (category: CategoryNode): boolean => {
    const hasActiveArticle = category.articles.some((article) => article.path === currentPath)

    if (hasActiveArticle) {
      parentPaths.push(category.path)
      return true
    }

    for (const child of category.children) {
      if (searchCategory(child)) {
        parentPaths.push(category.path)
        return true
      }
    }

    return false
  }

  for (const category of categoryTree) {
    searchCategory(category)
  }

  return parentPaths
}

function arePathSetsEqual(previous: Set<string>, next: Set<string>) {
  if (previous.size !== next.size) return false

  let areEqual = true
  previous.forEach((path) => {
    if (!next.has(path)) {
      areEqual = false
    }
  })

  return areEqual
}

export function DocumentationMenu({
  currentPath,
  categoryTree = [],
  rootArticles = [],
  basePath = ACADEMY_ROUTES.documentation,
  title = 'Documentation',
}: DocumentationMenuProps) {
  const { selectedLanguage } = useDocumentationLanguageContext()
  const parentCategoryPaths = useMemo(
    () => getParentCategoryPaths(categoryTree, currentPath),
    [currentPath, categoryTree]
  )

  const activeCategoryPaths = useMemo(() => new Set(parentCategoryPaths), [parentCategoryPaths])
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(() => new Set(activeCategoryPaths))

  useLayoutEffect(() => {
    setExpandedCategories((previous) => {
      if (arePathSetsEqual(previous, activeCategoryPaths)) {
        return previous
      }

      return new Set(activeCategoryPaths)
    })
  }, [activeCategoryPaths])

  const toggleCategory = (categoryPath: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryPath)) {
        next.delete(categoryPath)
      } else {
        next.add(categoryPath)
      }
      return next
    })
  }

  const isActive = (section: TutorialSection) => currentPath === section.path

  const buildArticleHref = (section: TutorialSection) => {
    const slug = documentationNavUrlPathForSection(section, selectedLanguage ?? undefined)
    return basePath ? `${basePath}/${slug}` : buildDocumentationHref(slug)
  }

  const renderCategory = (category: CategoryNode, level: number = 0) => {
    const isExpanded = expandedCategories.has(category.path)
    const hasContent = category.articles.length > 0 || category.children.length > 0
    const containsActive = activeCategoryPaths.has(category.path)

    if (!hasContent) return null

    return (
      <div key={category.path} className={styles.categoryWrapper}>
        <button
          className={`${styles.categoryHeader} ${containsActive ? styles.hasActive : ''}`}
          style={{ paddingLeft: `${0.5 + level * 0.5}rem` }}
          onClick={() => toggleCategory(category.path)}
          aria-expanded={isExpanded}
          aria-label={`Toggle ${category.title} category`}
        >
          <span className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}>
            <ChevronRightIcon />
          </span>
          <span className={styles.categoryTitle}>{category.title}</span>
        </button>

        {isExpanded && (
          <div className={styles.categoryContent}>
            {category.children.map((child) => renderCategory(child, level + 1))}

            {category.articles.length > 0 && (
              <ul className={styles.articleList}>
                {category.articles.map((section) => {
                  const active = isActive(section)
                  return (
                    <li key={section.path} className={`${styles.articleItem} ${active ? styles.active : ''}`}>
                      <a
                        href={buildArticleHref(section)}
                        className={styles.link}
                        style={{ paddingLeft: `${0.25 + (level + 1) * 1}rem` }}
                      >
                        <span className={styles.text}>{section.title}</span>
                      </a>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={styles.menu} data-testid={DOCUMENTATION_MENU_TEST_IDS.root}>
      <AcademyBackLink />
      <div className={styles.header}>
        <h3 className={styles.title}>{title}</h3>
      </div>

      {rootArticles.length > 0 && (
        <ul className={styles.list}>
          {rootArticles.map((section) => {
            const active = isActive(section)
            return (
              <li key={section.path} className={`${styles.item} ${active ? styles.active : ''}`}>
                <a href={buildArticleHref(section)} className={styles.link} aria-current={active ? 'page' : undefined}>
                  <span className={styles.text}>{section.title}</span>
                </a>
              </li>
            )
          })}
        </ul>
      )}

      {categoryTree.length > 0 && (
        <div className={styles.categoryTree}>{categoryTree.map((category) => renderCategory(category, 0))}</div>
      )}
    </div>
  )
}
